/**
 * CelebrateMemorialPrint.jsx — Printable memorial programme.
 *
 * A print-optimized single-page view of any memorial:
 *   • Portrait, name, dates
 *   • Biography excerpt / eulogy
 *   • QR code + short URL that visitors scan from a printed program
 *   • Family tree link (if attached)
 *
 * Route: /celebrate/:slug/print
 *
 * Uses browser "Save as PDF" — no server-side PDF generation needed
 * for the MVP. Print CSS hides all chrome (nav, buttons) so hitting
 * the browser Print button gives a keepsake-quality output.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import { Printer, ArrowLeft, Loader2 } from "lucide-react";
import SplinkCard from "@/components/SplinkCard";

const fmtDate = (d) => {
  if (!d) return "";
  const s = String(d);
  if (s.length === 4) return s;
  try {
    return new Date(s.slice(0, 10)).toLocaleDateString(undefined, {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch { return s; }
};

export default function CelebrateMemorialPrint() {
  const { slug } = useParams();
  const [m, setM] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await http.get(`/memorials/${slug}`);
        if (!alive) return;
        setM(data);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Memorial not found.");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center text-sm opacity-60">
      <Loader2 className="w-4 h-4 animate-spin mr-2" /> Preparing memorial…
    </div>
  );
  if (err || !m) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 p-8 text-center">
      <p className="text-lg">{err || "Not found."}</p>
      <Link to="/celebrate" className="wofy-btn-ghost text-sm"><ArrowLeft className="w-4 h-4" /> Back</Link>
    </div>
  );

  const name = m.full_name || m.preferred_name || "In Memoriam";
  const born = fmtDate(m.birth_date);
  const died = fmtDate(m.death_date);
  const bio = m.biography || m.english_snippet || m.short_bio || m.bio || "";
  const portrait = m.portrait || (m.portraits && m.portraits[m.primary_portrait_index || 0]);
  const loc = m.location || {};
  const cityCountry = [loc.city, loc.state, loc.country].filter(Boolean).join(", ");
  const publicUrl = typeof window !== "undefined"
    ? `${window.location.origin}/celebrate/${m.slug || slug}`
    : `/celebrate/${m.slug || slug}`;

  return (
    <div className="print-memorial min-h-screen" data-testid="celebrate-memorial-print">
      {/* Screen-only toolbar */}
      <div className="print-hide" style={{ borderBottom: "1px solid var(--wofy-line)", background: "var(--wofy-paper)" }}>
        <div className="max-w-3xl mx-auto p-4 flex items-center justify-between gap-3">
          <Link to={`/celebrate/${m.slug || slug}`} className="wofy-btn-ghost text-sm">
            <ArrowLeft className="w-4 h-4" /> Back to memorial
          </Link>
          <button onClick={() => window.print()} className="wofy-btn-primary text-sm" data-testid="print-memorial-btn">
            <Printer className="w-4 h-4" /> Print or Save as PDF
          </button>
        </div>
        <p className="max-w-3xl mx-auto px-4 pb-3 text-xs opacity-70">
          Tip: choose "Save as PDF" in your browser's print dialog. This memorial programme is designed for A4 / Letter paper and fits neatly on a single sheet.
        </p>
      </div>

      {/* Print-ready sheet */}
      <div className="print-sheet max-w-3xl mx-auto px-8 py-10">
        <div className="text-center mb-6">
          <div className="print-overline">In loving memory of</div>
          <h1 className="print-name">{name}</h1>
          {(born || died) && (
            <div className="print-dates">
              {born && <span>{born}</span>}
              {born && died && <span className="mx-3 opacity-60">✦</span>}
              {died && <span>{died}</span>}
            </div>
          )}
          {cityCountry && <div className="print-loc mt-1">{cityCountry}</div>}
        </div>

        {portrait && (
          <div className="flex justify-center mb-6">
            <img
              src={portrait}
              alt={name}
              className="print-portrait"
              crossOrigin="anonymous"
            />
          </div>
        )}

        {bio && (
          <section className="print-bio mb-6">
            <div className="print-section-title">Life &amp; Legacy</div>
            <p>{bio}</p>
          </section>
        )}

        {m.family_tree_slug && (
          <section className="mb-6 text-center text-sm">
            <div className="print-section-title">Family Tree</div>
            <p>
              This memorial is part of the <b>{m.family_tree_name || "family"}</b> tree on IndianFamilyTree.
            </p>
          </section>
        )}

        <div className="print-qr-block">
          <SplinkCard
            url={publicUrl}
            label={`Memorial of ${name}`}
            headline="Scan to visit this memorial online"
            testId="memorial-print-splink"
            className="print-splink"
          />
        </div>

        <footer className="print-footer text-center text-[10px] mt-8 pt-4 border-t opacity-70" style={{ borderColor: "#E9D8B4" }}>
          Preserved on IndianFamilyTree.com — a permanent digital record of {name.split(" ")[0]}'s life and legacy.
        </footer>
      </div>

      <style>{`
        .print-memorial { background: #FDFBF7; }
        .print-overline { font-family: 'Playfair Display', serif; font-style: italic; color: #7A5A2E; font-size: 14px; margin-bottom: 4px; }
        .print-name { font-family: 'Playfair Display', serif; font-size: 40px; line-height: 1.15; color: #1F1B16; margin-bottom: 8px; }
        .print-dates { font-family: 'Playfair Display', serif; font-size: 18px; color: #4A3E28; }
        .print-loc { font-size: 13px; color: #7A6A4E; }
        .print-portrait { max-width: 240px; max-height: 320px; border-radius: 8px; box-shadow: 0 4px 16px rgba(0,0,0,0.15); object-fit: cover; }
        .print-section-title {
          font-family: 'Playfair Display', serif; font-size: 12px;
          text-transform: uppercase; letter-spacing: 0.25em;
          color: #8B5E10; text-align: center; margin-bottom: 8px;
        }
        .print-bio p { font-size: 14px; line-height: 1.65; color: #2E2814; text-align: justify; }
        .print-qr-block { margin: 28px auto 0; max-width: 420px; }
        @media print {
          .print-hide { display: none !important; }
          body { background: white !important; }
          .print-sheet { padding: 24px !important; max-width: 100% !important; }
          .print-portrait { max-height: 260px !important; }
          .print-splink { border: 1px solid #B4864A !important; page-break-inside: avoid; }
          a[href] { color: inherit; text-decoration: none; }
        }
      `}</style>
    </div>
  );
}
